/**
 * 11. Setup Center — readiness score (0-100%), setup progress by category,
 * blocking items, continue-setup action.
 *
 * Reads GET /v1/health/tenant (the SPA calls it as /implementation/readiness
 * through the alias table in client.ts). The API names the list
 * `readinessSegments`; this page used to read `segments`, found nothing, and
 * so told every workspace — however unready — that it had no blocking items
 * and was ready to go live.
 *
 * Nothing here may claim readiness it has not been told: while loading or on
 * error there is no verdict, only a skeleton or the error with a Retry.
 */
import { Link } from "react-router-dom";
import { Card, PageHeader, Badge, Button } from "@/components/ui";
import { useApiQuery } from "@/lib/api/hooks";
import { formatPercent } from "@/lib/money";
import {
  errorMessage,
  useTranslation,
  type MessageKey,
  type TFunction,
} from "@/lib/i18n";

interface ReadinessSegment {
  phase: string;
  score: number;
  blocked: boolean;
  messages: string[];
  warnings?: string[];
}

interface ReadinessData {
  readinessScore: number;
  blockingErrors: number;
  warnings: number;
  readinessSegments: ReadinessSegment[];
}

/**
 * The API sends phase names and messages in English, not as codes
 * (kernel/tenancy/tenant-health.controller.ts). The known ones are translated
 * by exact text; a phase or message added later is shown as sent until it is
 * added to the catalogs.
 */
const KNOWN_MESSAGES: Record<string, MessageKey> = {
  "No active tax codes configured for this tenant": "setup.messages.noTaxCodes",
  "No active approval matrix; business documents cannot be approved":
    "setup.messages.noApprovalMatrix",
  "Postable chart of accounts is empty": "setup.messages.noPostableAccounts",
  "Opening balances cannot be entered until accounts exist":
    "setup.messages.openingBalancesNeedAccounts",
};

function phaseLabel(t: TFunction, phase: string): string {
  return t(`setup.phases.${phase}`, { defaultValue: phase });
}

function messageText(t: TFunction, message: string): string {
  const key = KNOWN_MESSAGES[message];
  return key ? t(key) : message;
}

export function SetupCenterPage() {
  const { t, i18n } = useTranslation();
  const { data, isError, error, refetch, isFetching } =
    useApiQuery<ReadinessData>(
      ["implementation", "readiness"],
      "/implementation/readiness",
    );

  const segments = data?.readinessSegments ?? [];
  const blocked = segments.filter((s) => s.blocked);
  const withWarnings = segments.filter(
    (s) => !s.blocked && (s.warnings?.length ?? 0) > 0,
  );
  const score = data?.readinessScore ?? 0;
  const blockingErrors = data?.blockingErrors ?? 0;
  const warnings = data?.warnings ?? 0;

  return (
    <div>
      <PageHeader
        title={t("setup.center.title")}
        subtitle={t("setup.center.subtitle")}
        actions={
          <Link className="atlas-btn atlas-btn--primary" to="/app/setup/wizard">
            {t("setup.center.continue")}
          </Link>
        }
      />
      {isError ? (
        <Card>
          <div role="alert" style={{ color: "var(--atlas-danger)" }}>
            {errorMessage(error, t, i18n.language)}
          </div>
          <div className="mt-4">
            <Button disabled={isFetching} onClick={() => void refetch()}>
              {t("common.tryAgain")}
            </Button>
          </div>
        </Card>
      ) : !data ? (
        <div className="atlas-skeleton" style={{ height: 120 }} />
      ) : (
        <>
          <div className="atlas-grid atlas-grid--2">
            <Card
              title={t("setup.center.score", { score: formatPercent(score) })}
            >
              <div
                style={{
                  background: "#eef1f3",
                  borderRadius: 8,
                  height: 14,
                  overflow: "hidden",
                  marginBlockEnd: 16,
                }}
              >
                <div
                  style={{
                    inlineSize: `${score}%`,
                    height: "100%",
                    background:
                      score >= 100
                        ? "var(--atlas-success)"
                        : "var(--atlas-primary)",
                  }}
                />
              </div>
              <Badge tone={blockingErrors > 0 ? "danger" : "success"}>
                {t("setup.center.blockingErrors", { count: blockingErrors })}
              </Badge>{" "}
              <Badge tone={warnings > 0 ? "warning" : "default"}>
                {t("setup.center.warnings", { count: warnings })}
              </Badge>
            </Card>
            <Card title={t("setup.center.categories")}>
              <table className="atlas-table">
                <tbody>
                  {segments.map((seg) => (
                    <tr key={seg.phase}>
                      <td>{phaseLabel(t, seg.phase)}</td>
                      <td style={{ textAlign: "end", width: 80 }}>
                        <Badge tone={seg.blocked ? "danger" : "success"}>
                          {formatPercent(seg.score)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
          <div className="mt-4">
            <Card
              title={t("setup.center.blockingItems")}
              actions={
                <Link
                  className="atlas-btn atlas-btn--sm"
                  to="/app/setup/audit-log"
                >
                  {t("setup.center.auditLog")}
                </Link>
              }
            >
              {blocked.length === 0 && (
                <div className="atlas-muted">
                  {t("setup.center.noBlockingItems")}
                </div>
              )}
              {blocked.map((s) => (
                <div key={s.phase} className="mb-2">
                  <Badge tone="danger">❌ {phaseLabel(t, s.phase)}</Badge>
                  <ul style={{ margin: "4px 0 0", fontSize: 13 }}>
                    {s.messages.map((m) => (
                      <li key={m}>{messageText(t, m)}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {withWarnings.map((s) => (
                <div key={s.phase} className="mb-2">
                  <Badge tone="warning">{phaseLabel(t, s.phase)}</Badge>
                  <ul style={{ margin: "4px 0 0", fontSize: 13 }}>
                    {(s.warnings ?? []).map((m) => (
                      <li key={m}>{messageText(t, m)}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
